import React, { useContext, useState } from 'react';
import { Form, Icon } from 'semantic-ui-react';

import { useDispatch, useSelector } from 'react-redux';

import { STATUS_TYPES, Notification, generateResolver, yup, VALIDATORS, ButtonLink } from 'dyl-components';

import contactPhoneActions from 'actions/contact_phone';
import contactActions from 'actions/contact';
import { useForm } from 'react-hook-form';
import { PhoneUtil, ValidationUtils } from 'utils';
import PhonesField from './PhonesField';
import EmailsField from './EmailsField';
import contactEmailActions from 'actions/contact_email';
import accountActions from 'actions/account';
import RestrictedView from 'shared/RestrictedView';
import { USER_PERMISSIONS } from 'dyl-components';
import { ArchivedRecordsContext } from 'shared/context/ArchivedRecordsProvider';

const Fields = ({ contact_id, account_id, spacing, account, contact, isContactInDialer }) => {
    const [fieldBeingEdited, setFieldBeingEdited] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const id = contact_id || account_id;
    const dispatch = useDispatch();

    const {
        phones,
        isCheckingDuplicates,
        emails
    } = useSelector((state) => {
        const phones = state.contact_phone.phone ? state.contact_phone.phone.slice(0) : [];
        const mainPhones = phones.filter(phone => phone.main);
        const nonMainPhones = phones.filter(phone => !phone.main);
        return {
            mainPhones,
            nonMainPhones,
            phones,
            isCheckingDuplicates: state.contact_duplicates.isCheckingDuplicates,
            isReadingPhones: state.contact_phone.isReadingPhones,
            emails: state.contact_email.email ? state.contact_email.email.slice(0) : []
        }
    })

    const schema = {
        phones: yup.array().of(yup.object().shape({
            value: VALIDATORS.PHONE_NUMBER().required('This field is required'),
            type: yup.string().required('This field is required')
        })).test('require-phone-if-no-email', 'Should have at least one phone', function(phones) {
            const emails = this.parent.emails ?? [];
            const hasEmail = emails?.some?.((email) => !!email.value);
            const hasPhone = phones?.some?.((phone) => !!phone.value);
            return hasPhone || hasEmail;
        }).test('no_repeating_phone', "Phone already in the list", ValidationUtils.checkForDuplicatePhonesInTheList),
        emails: yup.array().of(yup.object().shape({
            value: VALIDATORS.EMAIL_ADDRESS().maxlength(256).required('This field is required'),
            type: yup.string().required('This field is required')
        })).test('require-email-if-no-phone', 'Should have at least one email', function(emails) {
            const phones = this.parent.phones ?? [];
            const hasEmail = emails?.some?.((email) => !!email.value);
            const hasPhone = phones?.some?.((phone) => !!phone.value);
            return hasPhone || hasEmail;
        }).test('no_repeating_email', "Email already in the list", ValidationUtils.checkForDuplicateEmailsInTheList),
    }

    const defaultValues = {
        phones: phones.map(phone => ({
            value: phone.phone,
            main: phone.main,
            id: phone.id,
            type: phone.phone_type
        })),
        emails: emails.map(email => ({
            value: email.email,
            main: email.main,
            id: email.id,
            type: email.email_type
        }))
    }

    const { formState: { isDirty, isValid }, control, watch, setError, clearErrors, reset } = useForm({
        mode: 'onChange',
        defaultValues,
        resolver: generateResolver(schema)
    });

    const [watchedPhones, watchedEmails] = watch(['phones', 'emails']);

    const onCancel = () => {
        setFieldBeingEdited(false);
        reset({
            emails: emails.map(email => ({
                value: email.email,
                main: email.main,
                id: email.id,
                type: email.email_type
            })),
            phones: phones.map(phone => ({
                value: phone.phone,
                main: phone.main,
                id: phone.id,
                type: phone.phone_type
            }))
        })
    }

    const onUpdateContactFields = async (contact_id, emails, emailValues, phones, phoneValues) => {
        const emailsToDelete = emails.filter(email => emailValues.findIndex(emailValue => emailValue.id === email.id) === -1);
        const emailsToAdd = emailValues.filter(email => email.new);
        const emailsToUpdate = emailValues.filter(emailValue => !emailValue.new && emails.findIndex(email => (
            email.id === emailValue.id &&
            email.main === emailValue.main &&
            email.email === emailValue.value &&
            email.email_type === emailValue.type
        )) === - 1);

        const phonesToDelete = phones.filter(phone => phoneValues.findIndex(phoneValue => phoneValue.id === phone.id) === -1);
        const phonesToAdd = phoneValues.filter(phone => phone.new);
        const phonesToUpdate = phoneValues.filter(phoneValue => !phoneValue.new && phones.findIndex(phone => (
            phone.id === phoneValue.id &&
            phone.main === phoneValue.main &&
            phone.phone === phoneValue.value &&
            phone.phone_type === phoneValue.type
        )) === - 1);

        const emailTasks = [
            ...emailsToUpdate.filter(email => !email.main).map(email => {
                return dispatch(contactEmailActions.updateContactEmail(contact_id, {
                    main: email.main,
                    email: email.value,
                    email_type: email.type
                }, null, email.id));
            }),
            ...emailsToDelete.map(email => {
                return dispatch(contactEmailActions.deleteContactEmail(contact_id, null, email.id));
            }),
        ];
        if (emailsToAdd.length > 0) {
            emailTasks.push(
                dispatch(contactEmailActions.addContactEmail(emailsToAdd.map(email => ({
                    main: email.main,
                    email: email.value,
                    email_type: email.type
                })), null, contact_id))
            )
        }

        const phoneTasks = [
            ...phonesToUpdate.filter(phone => !phone.main).map(phone => {
                return dispatch(contactPhoneActions.updateContactPhone(contact_id, {
                    main: phone.main,
                    phone: phone.value,
                    phone_type: phone.type
                }, null, phone.id));
            }),
            ...phonesToDelete.map(phone => {
                return dispatch(contactPhoneActions.deleteContactPhone(contact_id, null, phone.id));
            }),
        ];
        if (phonesToAdd.length > 0) {
            phoneTasks.push(
                dispatch(contactPhoneActions.addContactPhone(phonesToAdd.map(phone => ({
                    main: phone.main,
                    phone: phone.value,
                    phone_type: phone.type
                })), null, contact_id))
            )
        }

        return Promise.all([...emailTasks, ...phoneTasks]).then(() => {
            const newMainEmail = emailsToUpdate.find(email => email.main);
            const newMainPhone = phonesToUpdate.find(phone => phone.main);
            return Promise.all([
                newMainEmail && dispatch(contactEmailActions.updateContactEmail(contact_id, {
                    main: newMainEmail.main,
                    email: newMainEmail.value,
                    email_type: newMainEmail.type
                }, null, newMainEmail.id)),
                newMainPhone && dispatch(contactPhoneActions.updateContactPhone(contact_id, {
                    main: newMainPhone.main,
                    phone: newMainPhone.value,
                    phone_type: newMainPhone.type
                }, null, newMainPhone.id))
            ])
        });
    }

    const onReadContactEmails = (contact_id) => {
        return dispatch(contactEmailActions.getContactEmails(contact_id));
    }

    const onReadAccount = (contact_id) => {
        return dispatch(accountActions.readAccount(contact_id));
    }

    const onReadContact = (contact_id) => {
        return dispatch(contactActions.readContact(contact_id));
    }
    
    const onReadContactPhones = (contact_id) => {
        return Promise.all([
            dispatch(contactActions.readContact(contact_id)),
            dispatch(contactPhoneActions.getContactPhones(contact_id)),
        ])
    }

    const onConfirmEdit = () => {
        setIsUpdating(true);
        try {
            onUpdateContactFields(id, emails, watchedEmails, phones, watchedPhones).then(() => {
                setIsUpdating(false);
                Notification.alert('Successfully updated phones and emails!', STATUS_TYPES.SUCCESS, true);
                const innerPromises = [];
                innerPromises.push(onReadContactPhones(id));
                innerPromises.push(onReadContactEmails(id));
                Promise.all(innerPromises).then(() => {
                    onReadAccount(id);
                    onReadContact(id);
                })
            });
        } catch (error) {
            setIsUpdating(false);
            Notification.alert('Failed to update phones and emails', STATUS_TYPES.ERROR, true);
        }
    }

    const onPhoneClick = ({ phone }) => {
        if (isContactInDialer) {
            return;
        }
        const numberContactId = contact_id ? Number(contact_id) : null;
        const numberAccountId = account_id ? Number(account_id) : null;
        PhoneUtil.C2D({
            payload: {
                contact_id: numberContactId,
                account_id: numberAccountId,
                destination: phone
            },
            dispatch 
        });
    }
    const {isArchivedView} = useContext(ArchivedRecordsContext);
    return (
        <>
            {fieldBeingEdited ? (
                <Form.Field>
                    <ButtonLink
                        className="ContactInfo__redButton"
                        style={spacing ? {margin: "7px 10px 7px 0px", padding: 0} : {}}
                        onClick={onCancel}
                        disabled={false}
                    >
                        <Icon className='fa-solid fa-xmark' />
                        Cancel
                    </ButtonLink>
                    <ButtonLink
                        style={{margin: spacing ? "0px, 10px, 0px" : 0, paddingLeft: 0}}
                        onClick={onConfirmEdit}
                        disabled={!isDirty || !isValid || isCheckingDuplicates}
                    >
                        <Icon className='fa-solid fa-check' />
                        Save
                    </ButtonLink>
                </Form.Field>

            ) : (
                !isArchivedView && <Form.Field>
                    <RestrictedView type="disabled" userPermission={account_id ? USER_PERMISSIONS.ACCOUNT_EDIT : USER_PERMISSIONS.CONTACTS_EDIT} userRestrict assignmentIds={account_id ? [(account?.assignees?.owner_id || 0), (account?.assignees?.coowner_id || 0)] : [(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}>
                        <ButtonLink style={{margin: spacing ? "7px 0px 7px" : 0, padding: 0}} onClick={() => setFieldBeingEdited(true)}>
                            <Icon className='fa-solid fa-pencil' />
                            Edit
                        </ButtonLink>
                    </RestrictedView>
                </Form.Field>
            )}
            <PhonesField
                isEditing={fieldBeingEdited}
                contact_id={id}
                account_id={account_id}
                control={control}
                setError={setError}
                clearErrors={clearErrors}
                isUpdating={isUpdating}
                watchedPhones={watchedPhones}
                spacing={spacing}
                onPhoneClick={onPhoneClick}
                allowNoPhones
            />
            <EmailsField
                isEditing={fieldBeingEdited}
                contact_id={id}
                account_id={account_id}
                control={control}
                setError={setError}
                clearErrors={clearErrors}
                isUpdating={isUpdating}
                watchedEmails={watchedEmails}
                spacing={spacing}
                allowNoEmails
            />
        </>
    );
};

export default Fields;
